import { Children, forwardRef, isValidElement, useCallback, useMemo, useRef, type ComponentPropsWithRef } from "react";

import { Command } from "cmdk";

import { composeEventHandlers } from "@radix-ui/primitive";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import { createContext } from "@radix-ui/react-context";
import * as Popover from "@radix-ui/react-popover";
import { Slot } from "@radix-ui/react-slot";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

/* -------------------------------------------------------------------------------------------------
 * Combobox
 * -----------------------------------------------------------------------------------------------*/

const COMBOBOX_NAME = "PrimitiveCombobox";

interface ComboboxContextValue {
  value: string;
  onValueChange: (value: string) => void;
  inputValue: string;
  onInputValueChange: (value: string, reason: "inputChange" | "itemSelect" | "clearClick") => void;
  onInputBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  disabled?: boolean;
  required?: boolean;
}

const [ComboboxProvider, useComboboxContext] = createContext<ComboboxContextValue>(COMBOBOX_NAME);

interface ComboboxProps
  extends
    Omit<React.ComponentProps<typeof Popover.Root>, "children">,
    Omit<React.ComponentProps<typeof Command>, "value" | "defaultValue" | "onValueChange" | "children"> {
  children?: React.ReactNode;
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  inputValue?: string;
  defaultInputValue?: string;
  onInputValueChange?: (inputValue: string, reason: "inputChange" | "itemSelect" | "clearClick") => void;
  onInputBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  required?: boolean;
}

function Combobox({
  open: openProp,
  onOpenChange,
  defaultOpen,
  modal,
  children,
  value: valueProp,
  defaultValue,
  onValueChange,
  inputValue: inputValueProp,
  defaultInputValue,
  onInputValueChange,
  onInputBlur,
  disabled,
  required,
  ...props
}: ComboboxProps) {
  const [value, setValue] = useControllableState({
    prop: valueProp,
    defaultProp: defaultValue ?? "",
    onChange: onValueChange,
  });

  const [inputValue, setInputValue] = useControllableState({
    prop: inputValueProp,
    defaultProp: defaultInputValue ?? "",
  });

  const [open, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen ?? false,
    onChange: onOpenChange,
  });

  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputValueChange: ComboboxContextValue["onInputValueChange"] = useCallback(
    (inputValue, reason) => {
      setInputValue(inputValue);
      onInputValueChange?.(inputValue, reason);
    },
    [setInputValue, onInputValueChange],
  );

  return (
    <ComboboxProvider
      value={value}
      onValueChange={setValue}
      inputValue={inputValue}
      onInputValueChange={handleInputValueChange}
      onInputBlur={onInputBlur}
      open={open}
      onOpenChange={setOpen}
      inputRef={inputRef}
      disabled={disabled}
      required={required}
    >
      <Popover.Root open={open} onOpenChange={setOpen} modal={modal}>
        <Command data-slot="combobox" {...props}>
          {children}
          {!open && <Command.List aria-hidden hidden />}
        </Command>
      </Popover.Root>
    </ComboboxProvider>
  );
}

Combobox.displayName = COMBOBOX_NAME;

/* -------------------------------------------------------------------------------------------------
 * Input
 * -----------------------------------------------------------------------------------------------*/

const INPUT_NAME = "PrimitiveComboboxInput";

type InputProps = Omit<React.ComponentProps<typeof Command.Input>, "value" | "onValueChange">;

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ onKeyDown, onMouseDown, onFocus, onBlur, ...props }, forwardedRef) => {
    const {
      inputValue,
      onInputValueChange,
      onInputBlur,
      open,
      onOpenChange,
      onValueChange,
      inputRef,
      disabled,
      required,
    } = useComboboxContext(INPUT_NAME);

    const composedRefs = useComposedRefs(inputRef, forwardedRef);

    return (
      <Command.Input
        data-slot="combobox-input"
        ref={composedRefs}
        disabled={disabled}
        required={required}
        value={inputValue}
        onValueChange={search => {
          if (!open) {
            onOpenChange(true);
          }
          // Schedule input value change to the next tick.
          setTimeout(() => onInputValueChange(search, "inputChange"));
          if (!search) {
            onValueChange("");
          }
        }}
        onKeyDown={composeEventHandlers(onKeyDown, event => {
          if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            if (!open) {
              event.preventDefault();
              onOpenChange(true);
            }
          }
        })}
        onMouseDown={composeEventHandlers(onMouseDown, () => onOpenChange(!!inputValue || !open))}
        onFocus={composeEventHandlers(onFocus, () => onOpenChange(true))}
        onBlur={composeEventHandlers(onBlur, event => {
          if (!event.relatedTarget?.hasAttribute("cmdk-list")) {
            onInputBlur?.(event);
          }
        })}
        {...props}
      />
    );
  },
);

Input.displayName = INPUT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Clear
 * -----------------------------------------------------------------------------------------------*/

const CLEAR_NAME = "PrimitiveComboboxClear";

interface ClearProps extends ComponentPropsWithRef<"button"> {
  asChild?: boolean;
}

const Clear = forwardRef<HTMLButtonElement, ClearProps>(({ asChild, onClick, ...props }, forwardedRef) => {
  const { value, onValueChange, inputValue, onInputValueChange } = useComboboxContext(CLEAR_NAME);
  const Comp = asChild ? Slot : "button";

  const isValueEmpty = !value;

  return (
    <Comp
      ref={forwardedRef}
      data-slot="combobox-clear"
      disabled={isValueEmpty && !inputValue}
      onClick={composeEventHandlers(onClick, () => {
        onValueChange("");
        onInputValueChange("", "clearClick");
      })}
      {...props}
    />
  );
});

Clear.displayName = CLEAR_NAME;

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

const CONTENT_NAME = "PrimitiveComboboxContent";

type ContentProps = React.ComponentProps<typeof Popover.Content>;

const Content = forwardRef<HTMLDivElement, ContentProps>(
  ({ onOpenAutoFocus, onCloseAutoFocus, onInteractOutside, ...props }, forwardedRef) => (
    <Popover.Content
      ref={forwardedRef}
      data-slot="combobox-content"
      onOpenAutoFocus={composeEventHandlers(onOpenAutoFocus, event => event.preventDefault())}
      onCloseAutoFocus={composeEventHandlers(onCloseAutoFocus, event => event.preventDefault())}
      onInteractOutside={composeEventHandlers(onInteractOutside, event => {
        if (event.target instanceof Element && event.target.hasAttribute("cmdk-input")) {
          event.preventDefault();
        }
      })}
      {...props}
    />
  ),
);

Content.displayName = CONTENT_NAME;

/* -------------------------------------------------------------------------------------------------
 * List
 * -----------------------------------------------------------------------------------------------*/

const LIST_NAME = "PrimitiveComboboxList";

type ListProps = React.ComponentProps<typeof Command.List>;

const List = forwardRef<HTMLDivElement, ListProps>((props, forwardedRef) => (
  <Command.List ref={forwardedRef} data-slot="combobox-list" {...props} />
));

List.displayName = LIST_NAME;

/* -------------------------------------------------------------------------------------------------
 * Item
 * -----------------------------------------------------------------------------------------------*/

const ITEM_NAME = "PrimitiveComboboxItem";

interface ItemContextValue {
  isSelected: boolean;
}

const [ItemProvider, useItemContext] = createContext<ItemContextValue>(ITEM_NAME);

interface ItemProps extends Omit<React.ComponentProps<typeof Command.Item>, "value"> {
  value: string;
}

const Item = forwardRef<HTMLDivElement, ItemProps>(
  ({ value: valueProp, children, onMouseDown, ...props }, forwardedRef) => {
    const { value, onValueChange, onInputValueChange, onOpenChange } = useComboboxContext(ITEM_NAME);

    const inputValue = useMemo(() => findComboboxItemText(children), [children]);

    const isSelected = value === valueProp;

    return (
      <ItemProvider isSelected={isSelected}>
        <Command.Item
          ref={forwardedRef}
          data-slot="combobox-item"
          onMouseDown={composeEventHandlers(onMouseDown, event => event.preventDefault())}
          onSelect={() => {
            onValueChange(valueProp);
            onInputValueChange(inputValue, "itemSelect");
            // Schedule open change to the next tick.
            setTimeout(() => onOpenChange(false));
          }}
          value={valueProp}
          keywords={inputValue ? [inputValue] : undefined}
          {...props}
        >
          {children}
        </Command.Item>
      </ItemProvider>
    );
  },
);

Item.displayName = ITEM_NAME;

/* -------------------------------------------------------------------------------------------------
 * ItemIndicator
 * -----------------------------------------------------------------------------------------------*/

const ITEM_INDICATOR_NAME = "PrimitiveComboboxItemIndicator";

interface IndicatorProps extends React.ComponentPropsWithRef<"span"> {
  asChild?: boolean;
}

const Indicator = forwardRef<HTMLSpanElement, IndicatorProps>(({ asChild, ...props }, forwardedRef) => {
  const { isSelected } = useItemContext(ITEM_INDICATOR_NAME);
  const Comp = asChild ? Slot : "span";

  if (!isSelected) {
    return null;
  }

  return <Comp ref={forwardedRef} data-slot="combobox-item-indicator" aria-hidden {...props} />;
});

Indicator.displayName = ITEM_INDICATOR_NAME;

/* -------------------------------------------------------------------------------------------------
 * ItemText
 * -----------------------------------------------------------------------------------------------*/

const ITEM_TEXT_NAME = "PrimitiveComboboxItemText";

interface ItemTextProps {
  children: string;
}

/**
 * Real DOM wrapper: dev tooling may inject data-* props on JSX; Fragment rejects them when spread via {...props}.
 */
const ItemText: React.FC<ItemTextProps> = ({ children }) => (
  <span data-slot="combobox-item-text" style={{ display: "contents" }}>
    {children}
  </span>
);

ItemText.displayName = ITEM_TEXT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

const TRIGGER_NAME = "PrimitiveComboboxTrigger";

type TriggerProps = React.ComponentProps<typeof Popover.Trigger>;

const Trigger = Popover.Trigger;

Trigger.displayName = TRIGGER_NAME;

/* -------------------------------------------------------------------------------------------------
 * Anchor
 * -----------------------------------------------------------------------------------------------*/

const ANCHOR_NAME = "PrimitiveComboboxAnchor";

type AnchorProps = React.ComponentProps<typeof Popover.Anchor>;

const Anchor = Popover.Anchor;

Anchor.displayName = ANCHOR_NAME;

/* -------------------------------------------------------------------------------------------------
 * Portal
 * -----------------------------------------------------------------------------------------------*/

const PORTAL_NAME = "PrimitiveComboboxPortal";

type PortalProps = React.ComponentProps<typeof Popover.Portal>;

const Portal = Popover.Portal;

Portal.displayName = PORTAL_NAME;

/* -------------------------------------------------------------------------------------------------
 * Empty
 * -----------------------------------------------------------------------------------------------*/

const EMPTY_NAME = "PrimitiveComboboxEmpty";

type EmptyProps = React.ComponentProps<typeof Command.Empty>;

const Empty = Command.Empty;

Empty.displayName = EMPTY_NAME;

/* -------------------------------------------------------------------------------------------------
 * Group
 * -----------------------------------------------------------------------------------------------*/

const GROUP_NAME = "PrimitiveComboboxGroup";

type GroupProps = React.ComponentProps<typeof Command.Group>;

const Group = Command.Group;

Group.displayName = GROUP_NAME;

function findComboboxItemText(children: React.ReactNode) {
  let text = "";

  Children.forEach(children, child => {
    if (text) {
      return;
    }

    if (isValidElement<{ children: React.ReactNode }>(child)) {
      if (child.type === ItemText || (child.type as any)?.displayName === ITEM_TEXT_NAME) {
        text = child.props.children as string;
      } else {
        text = findComboboxItemText(child.props.children);
      }
    }
  });

  return text;
}

export {
  Anchor,
  Clear,
  Content,
  Empty,
  Group,
  Indicator,
  Input,
  Item,
  ItemText,
  List,
  Portal,
  Combobox as Root,
  Trigger,
  useComboboxContext,
};
export type {
  AnchorProps,
  ClearProps,
  ComboboxProps,
  ContentProps,
  EmptyProps,
  GroupProps,
  IndicatorProps,
  InputProps,
  ItemProps,
  ItemTextProps,
  ListProps,
  PortalProps,
  TriggerProps,
};
